import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Box,
  Typography,
  Divider,
  alpha,
  useTheme,
  Avatar,
  CircularProgress,
} from "@mui/material";
import {
  Close as CloseIcon,
  Add as AddIcon,
  LocationOn as LocationOnIcon,
} from "@mui/icons-material";
import { useMutation, useQueryClient } from "react-query";
import { enqueueSnackbar } from "notistack";
import { createSubLocations } from "../../../api/services/settings";
import CustomInput from "../../FormFields/CustomInput";

const CreateAreaModal = ({
  locationId,
  onFormAdded,
  refetch,
  show,
  handleClose,
}) => {
  const theme = useTheme();
  const queryClient = useQueryClient();
  const [name, setName] = useState("");

  const postMutation = useMutation(createSubLocations, {
    onSuccess: () => {
      enqueueSnackbar("New Area added successfully", { variant: "success" });
      setName("");
      refetch();
      if (onFormAdded) {
        onFormAdded();
      }
      handleClose();
    },
    onError: () => {
      enqueueSnackbar("Failed to add Area", { variant: "error" });
      setName("");
    },
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    const data = { name: name, Location: parseInt(locationId) };
    postMutation.mutate(data);
  };

  return (
    <Dialog
      open={show}
      onClose={handleClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        sx: {
          borderRadius: "16px",
          boxShadow: "0px 24px 48px -12px rgba(16, 24, 40, 0.25)",
          overflow: "hidden",
        },
      }}
    >
      <DialogTitle sx={{ p: 0 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            p: 2.5,
            pb: 1.5,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Avatar
              sx={{
                width: 40,
                height: 40,
                bgcolor: alpha(theme.palette.primary.main, 0.08),
                color: theme.palette.primary.main,
              }}
            >
              <LocationOnIcon sx={{ fontSize: 20 }} />
            </Avatar>
            <Box>
              <Typography variant="h6" fontWeight={600} sx={{ color: "#1A1A2E" }}>
                Add New Area
              </Typography>
              <Typography variant="caption" sx={{ color: "#6B7280" }}>
                Add a new area to this location
              </Typography>
            </Box>
          </Box>
          <IconButton
            onClick={handleClose}
            size="small"
            sx={{
              color: "#9CA3AF",
              "&:hover": { bgcolor: "#F3F4F6", color: "#4B5563" },
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
        <Divider sx={{ opacity: 0.4 }} />
      </DialogTitle>

      <form onSubmit={handleSubmit}>
        <DialogContent sx={{ p: 2.5 }}>
          <CustomInput
            label="Area Name"
            placeholder="Enter area name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            fullWidth
          />
        </DialogContent>

        <DialogActions sx={{ p: 2.5, pt: 0, gap: 1.5 }}>
          <Button
            onClick={handleClose}
            variant="outlined"
            sx={{
              textTransform: "none",
              fontWeight: 500,
              borderRadius: "10px",
              borderColor: "#D0D5DD",
              color: "#344054",
              "&:hover": {
                bgcolor: "#F9FAFB",
                borderColor: "#98A2B3",
              },
            }}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={!name.trim() || postMutation.isLoading}
            sx={{
              textTransform: "none",
              fontWeight: 600,
              borderRadius: "10px",
              bgcolor: "#2469F0",
              px: 4,
              "&:hover": {
                bgcolor: "#1A5BD4",
                boxShadow: "0px 4px 12px rgba(36, 105, 240, 0.3)",
              },
            }}
          >
            {postMutation.isLoading ? <CircularProgress size={20} /> : "Add Area"}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default CreateAreaModal;